import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  ReconcileRunResultSchema,
  ReconciliationListResponseSchema,
  ReconciliationFlagSchema,
  type ReconcileRunResult,
  type ReconciliationFlag,
  type ReconciliationListResponse,
  type ReconciliationStatus,
  type ResolveFlagInput,
} from "@/api/types/reconciliation";

/** Query-key factory — mirrors useDashboard's/useCampaigns' convention. */
export const reconciliationKeys = {
  all: ["reconciliation"] as const,
  lists: () => [...reconciliationKeys.all, "list"] as const,
  list: (status: ReconciliationStatus) => [...reconciliationKeys.lists(), status] as const,
};

/** The "money to review" queue — GET /v2/reconciliation?status=. Defaults
 * to "open", the queue that actually needs a human. */
export function useReconciliationFlags(status: ReconciliationStatus = "open") {
  return useQuery<ReconciliationListResponse>({
    queryKey: reconciliationKeys.list(status),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/reconciliation?status=${status}`);
      return validateResponse(
        ReconciliationListResponseSchema,
        data,
        "GET /v2/reconciliation",
      );
    },
    staleTime: 15 * 1000,
  });
}

/** POST /v2/reconciliation/:id/resolve — resolves (or, with `ignore: true`,
 * dismisses) a flag. Invalidates every status list, since a flag leaving
 * "open" changes the count on the "resolved"/"ignored" views too. */
export function useResolveFlag() {
  const queryClient = useQueryClient();
  return useMutation<ReconciliationFlag, unknown, ResolveFlagInput>({
    mutationFn: async ({ id, note, ignore }) => {
      const { data } = await apiClient.post(`/v2/reconciliation/${id}/resolve`, {
        note: note ?? null,
        ignore: ignore ?? false,
      });
      return validateResponse(
        ReconciliationFlagSchema,
        data,
        "POST /v2/reconciliation/:id/resolve",
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKeys.all });
    },
  });
}

/** POST /v2/reconciliation/run — triggers an ad hoc reconcile sweep (the
 * same routine the nightly cron calls). Refreshes the open-flags list on
 * success since a sweep can add new rows. */
export function useRunReconcile() {
  const queryClient = useQueryClient();
  return useMutation<ReconcileRunResult, unknown, void>({
    mutationFn: async () => {
      const { data } = await apiClient.post("/v2/reconciliation/run", {});
      return validateResponse(ReconcileRunResultSchema, data, "POST /v2/reconciliation/run");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reconciliationKeys.all });
    },
  });
}
